import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { type StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import {
  Button,
  DisplayText,
  IllusEmpty_2,
  Image,
  LoadingPlaceholder,
  theme,
} from '@sg/garnish';

import { getEnvVars } from '@order/utils';

import { useCustomerSession } from '../../hooks';

export const StripeCustomerSheet = () => {
  const {
    setupIntentSecret,
    customerSessionClientSecret,
    isFetchingCustomerSession,
    isMissingCustomerSession,
  } = useCustomerSession();

  if (isFetchingCustomerSession) {
    return (
      <LoadingPlaceholder
        rows={4}
        rowHeight={[40, 40, 40, 64]}
        palette="darkGray"
      />
    );
  }

  if (isMissingCustomerSession) {
    return (
      <View style={styles.container}>
        <Image source={IllusEmpty_2} style={styles.illustration} />

        <DisplayText sizeMatch={['24']} style={styles.title}>
          <FormattedMessage {...messages.error} />
        </DisplayText>
      </View>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret: setupIntentSecret,
        customerSessionClientSecret,
      }}
    >
      <LoadedForm />
    </Elements>
  );
};

const LoadedForm = () => {
  const stripe = useStripe();
  const elements = useElements();

  const [canSubmit, setCanSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ─── Callbacks ───────────────────────────────────────────────────────────────

  const handleSubmit = useCallback(async () => {
    if (!stripe || !elements) return;

    setIsSubmitting(true);

    await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: `https://${window.location.host}/account/payment-and-gift-cards`,
      },
    });
  }, [stripe, elements]);

  // ─── Effects ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!stripe || !elements) return;

    const paymentElement = elements.getElement('payment');

    if (!paymentElement) return;

    const handlePaymentElementChange = (
      event: StripePaymentElementChangeEvent,
    ) => {
      const isCard = event.value.type === 'card';
      const isComplete = event.complete;
      const hasSavedPaymentMethod = event.value.payment_method?.id;

      setCanSubmit(isCard && isComplete && !hasSavedPaymentMethod);
    };

    paymentElement.on('change', handlePaymentElementChange);

    return () => {
      paymentElement.off('change', handlePaymentElementChange);
    };
  }, [elements, stripe]);

  return (
    <View style={styles.container}>
      <PaymentElement />

      {canSubmit ? (
        <Button
          palette="primary"
          size="large-wide"
          isLoading={isSubmitting}
          onPress={handleSubmit}
        >
          <FormattedMessage {...messages.save} />
        </Button>
      ) : null}
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  error: {
    defaultMessage: 'Something went wrong, try again in a few moments...',
    description: 'Stripe Customer Sheet | Error title',
  },
  save: {
    defaultMessage: 'Save card',
    description: 'Stripe Customer Sheet | Save CTA',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    gap: theme.spacing['6'],
    paddingBottom: theme.spacing['4'],
  },
  illustration: {
    width: 150,
    height: 150,
    alignSelf: 'center',
  },
  title: {
    textAlign: 'center',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const stripePublicKey = getEnvVars().STRIPE_PUBLIC_KEY;
const stripePromise = loadStripe(stripePublicKey);
